import type { AnySchema } from 'yup';
import * as yup from 'yup';

import { InputType } from 'core/enums';
import { numberSchemaWithTransform } from 'core/yup';
import {
  IPartFieldConfig,
  IPartFieldConfigUom,
  IPartFieldSavePayload,
  IPartFieldSaveValue,
  ISavePartParticularsPayload,
  PartFieldDataType,
} from 'parts/types';

const UOM_KEY_SUFFIX = '__uom';

const MULTI_VALUE_SEPARATOR = '|#|';

export const uomKeyFor = (fieldId: string | number): string => `${fieldId}${UOM_KEY_SUFFIX}`;

export const hasUom = (config: IPartFieldConfig): boolean => {
  if (config.config?.isWhitelist === 1) {
    return false;
  }
  if (config.dataType === PartFieldDataType.BOOLEAN) {
    return false;
  }

  const ids = config.config?.allowedUoms?.ids;

  return Array.isArray(ids) && ids.length > 0;
};

export const getUomOptions = (
  config: IPartFieldConfig
): Array<{ label: string; value: string }> => {
  const byName = config.config?.allowedUoms?.byName;

  if (!byName || Array.isArray(byName)) {
    return [];
  }

  return Object.values(byName as Record<string, IPartFieldConfigUom>).map((uom) => ({
    label: String(uom?.name ?? ''),
    value: String(uom?.id ?? ''),
  }));
};

const pickPrimitive = (value: IPartFieldConfig['value']): string => {
  if (Array.isArray(value)) {
    return value[0] ?? '';
  }

  return value ?? '';
};

// Saved value is "<value> <uomName>"; recover uom id from the trailing token, else first.
export const buildUomDefaultValue = (config: IPartFieldConfig): string => {
  const allowedUoms = config.config?.allowedUoms;
  const ids = allowedUoms?.ids;

  if (!Array.isArray(ids) || ids.length === 0) {
    return '';
  }

  const byName = allowedUoms?.byName;
  const raw = pickPrimitive(config.value);
  const tokens = raw.split(' ').filter(Boolean);
  const uomToken = tokens.length > 1 ? tokens[tokens.length - 1] : null;

  if (uomToken && byName && !Array.isArray(byName)) {
    const match = (byName as Record<string, IPartFieldConfigUom>)[uomToken];

    if (match?.id != null) {
      return String(match.id);
    }
  }

  return String(ids[0]);
};

export const inputTypeFor = (config: IPartFieldConfig): InputType => {
  const fieldConfig = config.config;

  if (fieldConfig?.isWhitelist === 1 && fieldConfig.allowedValues?.length) {
    return fieldConfig.allowMultiple ? InputType.AUTOCOMPLETE : InputType.SELECT;
  }
  if (config.dataType === PartFieldDataType.BOOLEAN) {
    return InputType.SWITCH;
  }
  if (
    config.dataType === PartFieldDataType.INTEGER ||
    config.dataType === PartFieldDataType.DECIMAL
  ) {
    return InputType.NUMBER_INPUT;
  }

  return InputType.TEXT_INPUT;
};

export const buildInputProps = (config: IPartFieldConfig): Record<string, unknown> => {
  const props: Record<string, unknown> = { label: config.name };
  const fieldConfig = config.config;

  if (fieldConfig?.isWhitelist === 1 && fieldConfig.allowedValues?.length) {
    if (fieldConfig.allowMultiple) {
      props.defaultOptions = fieldConfig.allowedValues.map((value) => ({
        name: String(value),
      }));
      props.optionKey = 'name';
      props.getLabel = (option: { name?: string } | null) => option?.name ?? '';
      props.multiple = true;
      props.value = [];
    } else {
      props.options = fieldConfig.allowedValues.map((value) => ({
        label: String(value),
        value: String(value),
      }));
      props.clearable = true;
    }
  }

  if (fieldConfig?.example) {
    props.placeholder = fieldConfig.example;
  }

  return props;
};

const buildDefaultValueFor = (config: IPartFieldConfig): unknown => {
  // Prefer valueNoUom — for UOM fields BE strips the trailing "<uomName>" token.
  const raw = config.valueNoUom ?? config.value;

  if (config.dataType === PartFieldDataType.BOOLEAN) {
    const primitive = pickPrimitive(raw);

    return primitive === '1' || primitive === 'true';
  }

  if (config.config?.isWhitelist === 1 && config.config.allowMultiple) {
    let names: string[] = [];

    if (Array.isArray(raw)) {
      names = raw.map(String);
    } else if (typeof raw === 'string' && raw.length > 0) {
      names = raw.split(MULTI_VALUE_SEPARATOR);
    }

    // AUTOCOMPLETE works on option-object identity; wrap names to match defaultOptions shape.
    return names.map((name) => ({ name }));
  }

  return pickPrimitive(raw);
};

const buildSchemaFor = (config: IPartFieldConfig): AnySchema => {
  const label = config.name ?? '';

  if (config.dataType === PartFieldDataType.BOOLEAN) {
    return yup.boolean().label(label).nullable();
  }
  if (config.config?.isWhitelist === 1 && config.config.allowMultiple) {
    return yup.array().of(yup.object()).label(label).nullable();
  }
  if (
    config.dataType === PartFieldDataType.INTEGER ||
    config.dataType === PartFieldDataType.DECIMAL
  ) {
    if (config.config?.isWhitelist === 1) {
      return yup.string().label(label).nullable();
    }

    return numberSchemaWithTransform().label(label).nullable();
  }

  return yup.string().label(label).nullable();
};

// Positional grouping: emits a new section every time the `group` value changes, so the same
// label appearing in two non-contiguous runs becomes two sections (matches legacy renderer).
export const groupByDisplayPosition = (
  configs: IPartFieldConfig[]
): Array<[string, IPartFieldConfig[]]> => {
  const sorted = [...configs].sort(
    (a, b) =>
      (a.displayPosition ?? Number.MAX_SAFE_INTEGER) -
      (b.displayPosition ?? Number.MAX_SAFE_INTEGER)
  );

  const sections: Array<[string, IPartFieldConfig[]]> = [];

  sorted.forEach((config) => {
    const groupKey = config.group ?? '';
    const currentSection = sections[sections.length - 1];

    if (currentSection && currentSection[0] === groupKey) {
      currentSection[1].push(config);

      return;
    }

    sections.push([groupKey, [config]]);
  });

  return sections;
};

export const buildDefaultValues = (configs: IPartFieldConfig[]): Record<string, unknown> => {
  const values: Record<string, unknown> = {};

  configs.forEach((config) => {
    if (config.fieldId == null) {
      return;
    }
    const key = String(config.fieldId);

    values[key] = buildDefaultValueFor(config);

    if (hasUom(config)) {
      values[uomKeyFor(config.fieldId)] = buildUomDefaultValue(config);
    }
  });

  return values;
};

export const buildSchema = (configs: IPartFieldConfig[]) => {
  const shape: Record<string, AnySchema> = {};

  configs.forEach((config) => {
    if (config.fieldId == null) {
      return;
    }
    const key = String(config.fieldId);

    shape[key] = buildSchemaFor(config);

    if (hasUom(config)) {
      shape[uomKeyFor(config.fieldId)] = yup.string().nullable();
    }
  });

  return yup.object().shape(shape);
};

const buildFieldSaveEntry = (
  config: IPartFieldConfig,
  formValues: Record<string, unknown>
): IPartFieldSavePayload => {
  const entry: IPartFieldSavePayload = { partFieldId: config.fieldId as number };
  const rawValue = formValues[String(config.fieldId)];

  if (config.dataType === PartFieldDataType.BOOLEAN) {
    entry.values = [{ value: rawValue ? '1' : '0' }];

    return entry;
  }

  if (config.config?.isWhitelist === 1 && config.config.allowMultiple) {
    if (Array.isArray(rawValue) && rawValue.length > 0) {
      entry.values = rawValue.map((option: { name?: string } | null) => ({
        value: String(option?.name ?? ''),
      }));
    }
    // BE contract: omitting `values` on multi-whitelist means clear-all (verified 2026-05-15).
    // Do not switch to `entry.values = []` — that's a different BE branch.

    return entry;
  }

  const stringValue = rawValue == null ? '' : String(rawValue);
  const value: IPartFieldSaveValue = { value: stringValue };

  if (hasUom(config)) {
    const uomValue = formValues[uomKeyFor(config.fieldId as number)];

    if (uomValue != null && uomValue !== '') {
      value.uomId = uomValue as string | number;
    }
  }

  entry.values = [value];

  return entry;
};

export const buildFieldNameMap = (configs: IPartFieldConfig[]): Map<string, number> => {
  const map = new Map<string, number>();

  configs.forEach((config) => {
    if (config.fieldId != null && config.name) {
      map.set(config.name, config.fieldId);
    }
  });

  return map;
};

export const buildSavePayload = (
  partNumber: string,
  manufacturerId: number,
  configs: IPartFieldConfig[],
  formValues: Record<string, unknown>
): ISavePartParticularsPayload => {
  const fields: Record<string, IPartFieldSavePayload> = {};

  configs.forEach((config) => {
    if (config.fieldId == null) {
      return;
    }
    fields[String(config.fieldId)] = buildFieldSaveEntry(config, formValues);
  });

  return {
    part: partNumber,
    manufacturer: manufacturerId,
    fields,
  };
};
